import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import PopSnackbar from "../../../../components/snackbar/Snackbar";
import CC417 from "./cc_components/CC417";
import CC503 from "./cc_components/CC503";
import CC413 from "./cc_components/CC413";
import CC504 from "./cc_components/CC504";
import CC414 from "./cc_components/CC414";
import CC446 from "./cc_components/CC446";
import {
  CreateCC417DeclarationH1,
  CreateCC503DeclarationH1,
  CreateCC413DeclarationH1,
  CreateCC504DeclarationH1,
  CreateCC414DeclarationH1,
  CreateCC446DeclarationH1,
  GetImportH1S2SDeclarationInfo,
  GetImportH1S2SDeclarationInfoForCC413,
  GetImportH1S2SDeclarationInfoForCC446,
} from "../../../../services/import/h1/s2s/create";

export default function UploadCCS2SH1({ selectedOfficeID }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [description, setDescription] = useState("");
  const [declarationInfo, setDeclarationInfo] = useState(null);

  const { declarationId, ccType, ccMessage } = location.state || {};

  async function submitForm(values) {
    try {
      setLoading(true);
      const bodyData = values.data || values;
      const jsonValues = JSON.stringify(bodyData);
      let response;

      switch (ccType) {
        case "CC417":
          response = await CreateCC417DeclarationH1(
            selectedOfficeID,
            jsonValues,
            declarationId
          );
          break;
        case "CC503":
          response = await CreateCC503DeclarationH1(
            selectedOfficeID,
            jsonValues,
            declarationId
          );
          break;
        case "CC413":
          response = await CreateCC413DeclarationH1(
            selectedOfficeID,
            jsonValues,
            declarationId
          );
          break;
        case "CC504":
          response = await CreateCC504DeclarationH1(
            selectedOfficeID,
            jsonValues,
            declarationId
          );
          break;
        case "CC414":
          response = await CreateCC414DeclarationH1(
            selectedOfficeID,
            jsonValues,
            declarationId
          );
          break;
        case "CC446":
          response = await CreateCC446DeclarationH1(
            selectedOfficeID,
            jsonValues,
            declarationId
          );
          break;
        default:
          throw new Error(`Unsupported CC type: ${ccType}`);
      }

      if (response.status !== "OK") {
        throw new Error(
          response.error || "Something went wrong, please try again later."
        );
      }

      handleStatus(
        "Declaration has been successfully submitted",
        setError,
        setDescription
      );
    } catch (error) {
      handleStatus(error.message, setError, setDescription);
    } finally {
      navigate(-1);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!declarationId || !ccType) {
      navigate(-1);
    }

    // Fetch declaration info for CC417, CC503, CC414, CC504, and CC446
    if (
      ccType === "CC417" ||
      ccType === "CC503" ||
      ccType === "CC504" ||
      ccType === "CC414"
    ) {
      getDeclarationInfo();
    } else if (ccType === "CC413") {
      getDeclarationInfoForCC413();
    } else if (ccType === "CC446") {
      getDeclarationInfoForCC446();
    }
  }, []);

  async function getDeclarationInfo() {
    try {
      setLoading(true);
      const response = await GetImportH1S2SDeclarationInfo(
        selectedOfficeID,
        declarationId
      );
      if (response.status !== "OK") {
        throw new Error(response.error || "Failed to fetch declaration info");
      }
      setDeclarationInfo(response.data);
    } catch (error) {
      handleStatus(error.message, setError, setDescription);
    } finally {
      setLoading(false);
    }
  }

  async function getDeclarationInfoForCC413() {
    try {
      setLoading(true);
      const response = await GetImportH1S2SDeclarationInfoForCC413(
        selectedOfficeID,
        declarationId
      );
      if (response.status !== "OK") {
        throw new Error(response.error || "Failed to fetch declaration info");
      }
      setDeclarationInfo(response.data);
    } catch (error) {
      handleStatus(error.message, setError, setDescription);
    } finally {
      setLoading(false);
    }
  }

  async function getDeclarationInfoForCC446() {
    try {
      setLoading(true);
      const response = await GetImportH1S2SDeclarationInfoForCC446(
        selectedOfficeID,
        declarationId
      );
      if (response.status !== "OK") {
        throw new Error(response.error || "Failed to fetch declaration info");
      }
      setDeclarationInfo({
        ...response.data,
        cc438_info_list: response.cc438_info || [],
      });
    } catch (error) {
      handleStatus(error.message, setError, setDescription);
    } finally {
      setLoading(false);
    }
  }

  const handleStatus = (description, setStatus, setDescription) => {
    setStatus(true);
    setDescription(description);
    setTimeout(() => {
      setStatus(false);
      setDescription("");
    }, 5000);
  };

  const switchCCDeclaration = (ccType) => {
    switch (ccType) {
      case "CC417":
        return (
          <CC417 submitForm={submitForm} declarationInfo={declarationInfo} />
        );
      case "CC503":
        return (
          <CC503 submitForm={submitForm} declarationInfo={declarationInfo} />
        );
      case "CC413":
        return (
          <CC413 submitForm={submitForm} declarationInfo={declarationInfo} />
        );
      case "CC504":
        return (
          <CC504 submitForm={submitForm} declarationInfo={declarationInfo} />
        );
      case "CC414":
        return (
          <CC414 submitForm={submitForm} declarationInfo={declarationInfo} />
        );
      case "CC446":
        return (
          <CC446 submitForm={submitForm} declarationInfo={declarationInfo} />
        );
      default:
        return <div>Unsupported CC type: {ccType}</div>;
    }
  };

  if (!declarationId || !ccType) {
    return <div>Work in progress!</div>;
  }

  return (
    <div>
      <h1 style={{ fontFamily: "SeagoeUIBold" }}>
        {ccType} - H1 -{" "}
        {ccMessage?.normalize("NFD").replace(/[\u0300-\u036f]/g, "") ||
          "Import Declaration S2S"}
      </h1>

      {error && <PopSnackbar message={description} />}

      {loading ? (
        <CircularProgress style={{ alignSelf: "center" }} />
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 10,
            marginTop: 20,
            backgroundColor: "transparent",
          }}
        >
          {switchCCDeclaration(ccType)}
        </div>
      )}
    </div>
  );
}
